import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Button, Divider, Grid, Tooltip, Typography } from '@mui/material';
import { green, red } from '@mui/material/colors';
import { Box } from '@mui/system';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { Announcement } from '@mui/icons-material';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { isValidElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { MemberDetailsSummary } from 'src/modules/member/components/memberDetailsSummary';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { redeemEventParticipantTicket } from 'src/shared/services/eventParticipantTicketService';
import { toast } from 'react-toastify';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { EventParticipantStatuses } from './eventParticipantStatuses';
import { EventParticipant } from '../models/eventParticipant';
import { EventParticipantTicket } from '../models/eventParticipantTicket';

type EventParticipantsDetailModalProps = {
  modalOpen: boolean;
  participant: EventParticipant;
  onClose: () => void;
  handleTicket?: EventParticipantTicket;
  refetchData?: () => void;
};

export const EventParticipantsDetailModal = (props: EventParticipantsDetailModalProps) => {
  const { modalOpen, participant, onClose, handleTicket = undefined, refetchData } = props;
  const { t } = useTranslation(['event']);
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const hasTicket = Boolean(handleTicket?.ticket);

  const redeemTicket = async () => {
    if (handleTicket && handleTicket.ticket) {
      setLoading(true);
      try {
        await redeemEventParticipantTicket(handleTicket.id);

        if (isMounted()) {
          toast.success(<>{t('event:ticketRedeemedSuccess')}</>);
          if (refetchData) {
            refetchData();
          }
        }
      } catch (e) {
        ConsoleHelper.log(e);
      } finally {
        if (isMounted()) {
          setShowConfirmDialog(false);
          setLoading(false);
        }
      }
    }
  };

  const ShowDetail = ({
    label,
    value,
  }: {
    label: string;
    value: number | string | Array<string | null | undefined> | null | undefined | JSX.Element;
  }) => {
    let displayValue: string | JSX.Element | null = null;

    // Hantera om `value` är en array av strängar
    if (Array.isArray(value)) {
      displayValue = value.filter((item): item is string => Boolean(item?.trim())).join(', ');
    }
    // Hantera om `value` är en sträng
    else if (typeof value === 'string') {
      displayValue = value.trim() || null;
    }
    // Hantera om `value` är number
    else if (typeof value === 'number') {
      displayValue = `${value}`;
    }
    // Hantera om `value` är ett giltigt JSX-element
    else if (isValidElement(value)) {
      displayValue = value;
    }
    return (
      <Typography variant='body2' sx={{ ml: 1 }}>
        <Typography variant='body2' color='text.secondary' component='span' sx={{ pr: 0.5 }}>
          {label}:
        </Typography>
        {displayValue || (
          <Typography variant='caption' fontStyle='italic' color='text.disabled' component='em'>
            {t('event:noInfo')}
          </Typography>
        )}
      </Typography>
    );
  };

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={`${participant.firstName} ${participant.lastName}`}
      dialogSubTitle={participant.memberNr ? `${t('event:tableHeaders.memberNr')}: ${participant.memberNr}` : undefined}
      dialogTitleContent={
        <Box sx={{ float: 'right', mr: 7 }}>
          <EventParticipantStatuses
            status={hasTicket ? undefined : participant.status}
            ticketRedeemed={hasTicket ? handleTicket?.isRedeemed : undefined}
            chipSize='small'
          />
        </Box>
      }
      disableEnforceFocus
      maxWidth='sm'
      onClose={() => onClose()}
      onSave={() => onClose()}
      showDialogActions={false}
      dialogContent={
        <>
          {participant.allergy && (
            <>
              <Alert severity='warning' icon={<Announcement />}>
                <AlertTitle>
                  {t('event:tableHeaders.allergies')}: {participant.allergy}
                </AlertTitle>
              </Alert>
              <Divider />
            </>
          )}
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant='subtitle1'>{t('event:contactInfo')}</Typography>
                <Box sx={{ ml: 1 }}>
                  <MemberDetailsSummary member={participant} showHeader={false} small />
                </Box>

                <Typography variant='subtitle1' sx={{ mt: 2 }}>
                  {t('event:created')}
                </Typography>
                <ShowDetail label={t('event:createdAt')} value={DateHelper.getDateTime(participant.createdAt)} />
                <ShowDetail label={t('event:createdByName')} value={participant.createdByName} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ backgroundColor: (thm) => thm.palette.grey[200], borderRadius: 1, p: 2, pt: 1.5 }}>
                  <Typography variant='subtitle1'>{t('event:tableHeaders.invoice')}</Typography>
                  {participant.invoiceStatus && <ShowDetail label={t('event:tableHeaders.status')} value={participant.invoiceStatus} />}
                  <ShowDetail label={t('event:tableHeaders.amount')} value={<AmountWithCurrency amount={participant.invoiceAmount || 0} />} />
                  {participant.invoiceOcr && (
                    <ShowDetail
                      label={t('event:ocr')}
                      value={
                        <Tooltip title={t('event:goToInvoice')} arrow disableInteractive>
                          <Link to={`${RoutesEnum.InvoicesDetail}/${participant.invoiceId}`}>{participant.invoiceOcr}</Link>
                        </Tooltip>
                      }
                    />
                  )}
                  <ShowDetail label={t('event:id')} value={participant.invoiceId} />
                </Box>
                {hasTicket && handleTicket?.ticket && (
                  <Box
                    sx={{
                      backgroundColor: handleTicket?.isRedeemed ? green[100] : red[100],
                      borderRadius: 1,
                      p: 2,
                      pt: 1.5,
                      mt: 2,
                    }}
                  >
                    <Typography variant='subtitle1'>{t('event:tableHeaders.ticket')}</Typography>
                    <ShowDetail label={t('event:ticketNr')} value={handleTicket.eventTicketNr} />
                    <ShowDetail label={t('event:tableHeaders.ticket')} value={handleTicket.ticket.ticketName} />
                    <ShowDetail
                      label={t('event:isRedeemed')}
                      value={handleTicket.redeemedAt ? DateHelper.getDateTime(handleTicket.redeemedAt) : t('event:notRedeemed')}
                    />
                    <ShowDetail label={t('event:redeemedBy')} value={handleTicket.redeemedByName ?? ''} />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
          <Box>
            <Divider />
            <Box
              sx={{
                display: 'flex',
                justifyContent: hasTicket ? 'space-between' : 'flex-end',
                p: 1,
              }}
            >
              {hasTicket && (
                <Button
                  variant='contained'
                  color='success'
                  disableElevation
                  disabled={loading || handleTicket?.isRedeemed === true}
                  onClick={() => setShowConfirmDialog(true)}
                >
                  {handleTicket?.isRedeemed === true ? t('event:isRedeemed') : t('event:redeemTicket')}
                </Button>
              )}
              <Button variant='contained' disableElevation onClick={() => onClose()}>
                {t('common:button.close')}
              </Button>
            </Box>
          </Box>
          {showConfirmDialog && (
            <DeleteConfirmationDialog
              open={showConfirmDialog}
              confirmButtonText={t('event:redeemTicket')}
              confirmButtonColor='success'
              title={t('event:redeemTicket')}
              value=''
              modaltext={t('event:confirmRedeemTicket')}
              onClose={(value) => {
                if (value) {
                  redeemTicket();
                } else {
                  setShowConfirmDialog(false);
                }
              }}
            />
          )}
        </>
      }
    />
  );
};
